// src/palettes/builtin.ts
import type { Palette, RGB } from '../core/types';

const hex = (h: string): RGB => ({
  r: parseInt(h.slice(1, 3), 16),
  g: parseInt(h.slice(3, 5), 16),
  b: parseInt(h.slice(5, 7), 16),
});

const grays = (n: number): RGB[] =>
  Array.from({ length: n }, (_, i) => {
    const v = Math.round((i * 255) / (n - 1));
    return { r: v, g: v, b: v };
  });

export const BUILTIN_PALETTES: Palette[] = [
  { id: 'bw', label: 'Black & White', colors: grays(2) },
  { id: 'grayscale-4', label: '4-tone Grayscale', colors: grays(4) },
  { id: 'grayscale-8', label: '8-tone Grayscale', colors: grays(8) },
  {
    id: 'gameboy',
    label: 'Game Boy (DMG)',
    colors: ['#0f380f', '#306230', '#8bac0f', '#9bbc0f'].map(hex),
  },
  {
    id: 'gameboy-pocket',
    label: 'Game Boy Pocket',
    colors: ['#2b2b2b', '#5a5a5a', '#a4a4a4', '#e0e0e0'].map(hex),
  },
  {
    id: 'cga-0',
    label: 'CGA Mode 4 Pal 0',
    colors: ['#000000', '#00aaaa', '#aa00aa', '#aaaaaa'].map(hex),
  },
  {
    id: 'cga-1',
    label: 'CGA Mode 4 Pal 1',
    colors: ['#000000', '#55ffff', '#ff55ff', '#ffffff'].map(hex),
  },
  {
    id: 'ega',
    label: 'EGA',
    colors: [
      '#000000','#0000aa','#00aa00','#00aaaa','#aa0000','#aa00aa','#aa5500','#aaaaaa',
      '#555555','#5555ff','#55ff55','#55ffff','#ff5555','#ff55ff','#ffff55','#ffffff',
    ].map(hex),
  },
  {
    id: 'apple-ii',
    label: 'Apple II HiRes',
    colors: ['#000000','#6a3eff','#ff5cff','#0088ff','#00ff88','#ffffff'].map(hex),
  },
  {
    id: 'zx-spectrum',
    label: 'ZX Spectrum',
    colors: [
      '#000000','#0022c7','#d62816','#d433c7','#00c525','#00c7c9','#ccc82a','#cacaca',
      '#000000','#002bfb','#ff331c','#ff40fc','#00f92f','#00fbff','#fffc36','#ffffff',
    ].map(hex).slice(0, 15),
  },
  {
    id: 'c64',
    label: 'Commodore 64',
    colors: [
      '#000000','#ffffff','#880000','#aaffee','#cc44cc','#00cc55','#0000aa','#eeee77',
      '#dd8855','#664400','#ff7777','#333333','#777777','#aaff66','#0088ff','#bbbbbb',
    ].map(hex),
  },
  {
    id: 'msx',
    label: 'MSX (Screen 2)',
    colors: [
      '#000000','#3eb849','#74d07d','#5955e0','#8076f1','#b95e51','#65dbef','#db6559',
      '#ff897d','#ccc35e','#ded087','#3aa241','#b766b5','#cccccc','#ffffff',
    ].map(hex),
  },
  {
    id: 'pico-8',
    label: 'PICO-8',
    colors: [
      '#000000','#1d2b53','#7e2553','#008751','#ab5236','#5f574f','#c2c3c7','#fff1e8',
      '#ff004d','#ffa300','#ffec27','#00e436','#29adff','#83769c','#ff77a8','#ffccaa',
    ].map(hex),
  },
  {
    id: 'nes',
    label: 'NES (subset)',
    colors: [
      '#7c7c7c','#0000fc','#0000bc','#4428bc','#940084','#a80020','#a81000','#881400',
      '#503000','#007800','#006800','#005800','#004058','#000000','#bcbcbc','#0078f8',
      '#0058f8','#6844fc','#d800cc','#e40058','#f83800','#e45c10','#ac7c00','#00b800',
      '#00a800','#00a844','#008888','#f8f8f8','#3cbcfc','#6888fc','#9878f8','#f878f8',
      '#f85898','#f87858','#fca044','#f8b800','#b8f818','#58d854','#58f898','#00e8d8',
      '#787878','#fcfcfc','#a4e4fc','#b8b8f8','#d8b8f8','#f8b8f8','#f8a4c0','#f0d0b0',
      '#fce0a8','#f8d878','#d8f878','#b8f8b8','#b8f8d8','#00fcfc','#f8d8f8','#000000',
    ].map(hex),
  },
];

export function getPalette(id: string): Palette | undefined {
  return BUILTIN_PALETTES.find(p => p.id === id);
}
